import React, { createContext, useContext, useState, useCallback } from 'react';

export type SystemAppMode = 'demo' | 'live';

const STORAGE_KEY = '__firesquirrel_system_mode__';

function isDemoHostname(): boolean {
  try {
    const host = window.location.hostname;
    return host.startsWith('demo.') || host === 'demo';
  } catch { return false; }
}

function isDemoQueryParam(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('demomode') === 'true';
  } catch { return false; }
}

function readPersistedMode(): SystemAppMode {
  // demo. subdomain always forces demo regardless of anything else
  if (isDemoHostname()) return 'demo';
  // ?demomode=true query param forces demo mode
  if (isDemoQueryParam()) return 'demo';
  // Check if user has explicitly set a preference
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'live') return 'live';
    if (v === 'demo') return 'demo';
  } catch { /* ignore */ }
  // Default to live mode — demo is only triggered by subdomain or ?demomode=true
  return 'live';
}

interface SystemAppModeState {
  mode: SystemAppMode;
  isDemoMode: boolean;
  isLiveMode: boolean;
  setMode: (next: SystemAppMode) => void;
}

const SystemAppModeContext = createContext<SystemAppModeState>({
  mode: 'demo',
  isDemoMode: true,
  isLiveMode: false,
  setMode: () => {},
});

export function SystemAppModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<SystemAppMode>(readPersistedMode);

  const setMode = useCallback((next: SystemAppMode) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const value: SystemAppModeState = {
    mode,
    isDemoMode: mode === 'demo',
    isLiveMode: mode === 'live',
    setMode,
  };

  return (
    <SystemAppModeContext.Provider value={value}>
      {children}
    </SystemAppModeContext.Provider>
  );
}

export function useSystemAppMode(): SystemAppModeState {
  return useContext(SystemAppModeContext);
}
